import React, { useEffect, useState } from 'react';
import { aiUsageAPI } from '../../services/api';

interface FeatureRow { _id: string; total: number; calls: number; }
interface DayRow { _id: string; total: number; calls: number; }
interface RecentRow {
  _id: string;
  feature: string;
  model: string;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  createdAt: string;
  project?: { name?: string } | null;
  user?: { firstName?: string; lastName?: string; email?: string } | null;
}

interface Summary {
  totals: { prompt: number; completion: number; total: number; calls: number };
  byFeature: FeatureRow[];
  byModel: FeatureRow[];
  byDay: DayRow[];
  recent: RecentRow[];
}

const FEATURE_LABELS: Record<string, string> = {
  assistant: 'AI-assistent',
  document: 'Documentverwerking',
  projectverkenning: 'Projectverkenning (AI)',
  omgevingsanalyse: 'Omgevingsanalyse (AI)',
  personas: "Persona's (AI)",
  strategie: 'Communicatiestrategie (AI)',
  kernboodschap: 'Kernboodschap pretest (AI)',
  sentiment: 'Nieuws & sentiment (AI)',
  werkplaats: 'Communicatiewerkplaats (AI)',
  zoektermen: 'Zoektermen voorstellen (AI)',
  other: 'Overig',
};

const nf = (n: number) => (n || 0).toLocaleString('nl-NL');

const card: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, background: '#fff' };

export const AiUsagePage: React.FC = () => {
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await aiUsageAPI.getSummary();
        setData(res);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Kon het tokengebruik niet laden.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Tokengebruik laden…</div>;
  if (error) return <div style={{ padding: 32, color: '#b91c1c' }}>{error}</div>;
  if (!data) return null;

  const maxDay = Math.max(1, ...data.byDay.map((d) => d.total));

  const tiles = [
    { label: 'Totaal tokens', value: nf(data.totals.total) },
    { label: 'AI-aanroepen', value: nf(data.totals.calls) },
    { label: 'Prompt-tokens', value: nf(data.totals.prompt) },
    { label: 'Completion-tokens', value: nf(data.totals.completion) },
  ];

  return (
    <div style={{ padding: 32, maxWidth: 1000 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, color: '#1a1a2e' }}>AI-tokengebruik</h1>
      <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: 14 }}>
        Verbruik van de AI-assistent en de documentverwerking. Gebruik dit om het tokenbudget te monitoren.
      </p>

      {/* Totalen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 20 }}>
        {tiles.map((t) => (
          <div key={t.label} style={card}>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 6 }}>{t.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>{t.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Per functie */}
        <div style={card}>
          <h2 style={{ margin: '0 0 12px', fontSize: 15, color: '#1a1a2e' }}>Per functie</h2>
          {data.byFeature.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>Nog geen verbruik.</div>}
          {data.byFeature.map((f) => (
            <div key={f._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f2f5', fontSize: 13.5 }}>
              <span style={{ color: '#374151' }}>{FEATURE_LABELS[f._id] || f._id}</span>
              <span style={{ color: '#1a1a2e', fontWeight: 600 }}>{nf(f.total)} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({f.calls}×)</span></span>
            </div>
          ))}
        </div>

        {/* Per model */}
        <div style={card}>
          <h2 style={{ margin: '0 0 12px', fontSize: 15, color: '#1a1a2e' }}>Per model</h2>
          {data.byModel.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>Nog geen verbruik.</div>}
          {data.byModel.map((m) => (
            <div key={m._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f2f5', fontSize: 13.5 }}>
              <span style={{ color: '#374151' }}>{m._id}</span>
              <span style={{ color: '#1a1a2e', fontWeight: 600 }}>{nf(m.total)} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({m.calls}×)</span></span>
            </div>
          ))}
        </div>
      </div>

      {/* Per dag (laatste 30 dagen) */}
      <div style={{ ...card, marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 15, color: '#1a1a2e' }}>Verbruik per dag (laatste 30 dagen)</h2>
        {data.byDay.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Nog geen verbruik in de afgelopen 30 dagen.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {data.byDay.map((d) => (
              <div key={d._id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                <span style={{ width: 82, color: '#6b7280', flexShrink: 0 }}>{d._id}</span>
                <div style={{ flex: 1, background: '#f1f2f5', borderRadius: 5, height: 16, overflow: 'hidden' }}>
                  <div style={{ width: `${(d.total / maxDay) * 100}%`, height: '100%', background: '#1a1a2e', borderRadius: 5 }} />
                </div>
                <span style={{ width: 90, textAlign: 'right', color: '#374151', flexShrink: 0 }}>{nf(d.total)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recente aanroepen */}
      <div style={card}>
        <h2 style={{ margin: '0 0 12px', fontSize: 15, color: '#1a1a2e' }}>Recente aanroepen</h2>
        {data.recent.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Nog geen aanroepen geregistreerd.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Datum</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Functie</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Model</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Project</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Tokens</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r._id} style={{ borderTop: '1px solid #f1f2f5' }}>
                    <td style={{ padding: '6px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {new Date(r.createdAt).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '6px 8px', color: '#374151' }}>{FEATURE_LABELS[r.feature] || r.feature}</td>
                    <td style={{ padding: '6px 8px', color: '#6b7280' }}>{r.model}</td>
                    <td style={{ padding: '6px 8px', color: '#374151' }}>{r.project?.name || '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#1a1a2e', fontWeight: 600 }}>{nf(r.totalTokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
